/**
 * Transcription History Panel
 * Search and browse past scanner transcriptions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  FileText,
  Clock,
  Radio,
  AlertTriangle,
  Shield,
  Filter,
  Calendar,
  MapPin,
  X,
  Loader2,
  History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority colors
const PRIORITY_COLORS = {
  critical: 'bg-red-500 text-white',
  urgent: 'bg-orange-500 text-white',
  routine: 'bg-blue-500 text-white',
  informational: 'bg-slate-500 text-white'
};

const PRIORITY_ICONS = {
  critical: AlertTriangle,
  urgent: AlertTriangle,
  routine: Shield,
  informational: Radio
};

const TranscriptHistoryCard = ({ transcript, onClick }) => {
  const priorityClass = PRIORITY_COLORS[transcript.priority] || PRIORITY_COLORS.informational;
  const PriorityIcon = PRIORITY_ICONS[transcript.priority] || Radio;
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div 
      className="p-3 rounded-lg border border-slate-700 bg-slate-800/50 mb-3 hover:border-teal-500/50 cursor-pointer transition-colors"
      onClick={() => onClick?.(transcript)}
      data-testid="transcript-history-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-teal-400" />
          <span className="text-sm font-medium text-white truncate max-w-[200px]">
            {transcript.scanner_name}
          </span>
        </div>
        <Badge className={`${priorityClass} text-[10px] px-2`}>
          <PriorityIcon className="w-3 h-3 mr-1" />
          {transcript.priority?.toUpperCase()}
        </Badge>
      </div>
      
      {/* Text preview */}
      <p className="text-sm text-slate-300 line-clamp-2 mb-2">
        {transcript.text}
      </p>
      
      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatTime(transcript.timestamp)}
        </div>
        {transcript.keywords?.length > 0 && (
          <div className="flex gap-1">
            {transcript.keywords.slice(0, 3).map((kw, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-slate-700 rounded text-slate-400">
                {kw}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TranscriptDetailModal = ({ transcript, onClose }) => {
  if (!transcript) return null;
  
  const priorityClass = PRIORITY_COLORS[transcript.priority] || PRIORITY_COLORS.informational;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700 rounded-lg w-[500px] max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">{transcript.scanner_name}</h3>
            <p className="text-sm text-slate-400">
              {new Date(transcript.timestamp).toLocaleString()}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Content */}
        <ScrollArea className="h-[400px] p-4">
          {/* Priority & Keywords */}
          <div className="flex items-center gap-2 mb-4">
            <Badge className={priorityClass}>
              {transcript.priority?.toUpperCase()}
            </Badge>
            {transcript.keywords?.map((kw, i) => (
              <Badge key={i} variant="outline" className="text-slate-400 border-slate-600">
                {kw}
              </Badge>
            ))}
          </div>
          
          {/* Transcript Text */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-slate-400 mb-2">Transcript</h4>
            <p className="text-white bg-slate-800 p-3 rounded-lg">
              {transcript.text}
            </p>
          </div>
          
          {/* Tank's Analysis */}
          {transcript.analysis && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-slate-400 mb-2">Tank's Analysis</h4>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
                <p className="text-slate-300 text-sm mb-2">
                  {transcript.analysis.summary || 'No summary available'}
                </p>
                {transcript.analysis.tank_assessment && (
                  <p className="text-teal-400 text-sm italic">
                    "{transcript.analysis.tank_assessment}"
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Incidents */}
          {transcript.incidents_detected?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-2">Incidents Detected</h4>
              <div className="space-y-2">
                {transcript.incidents_detected.map((incident, i) => (
                  <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                    <span className="text-red-400 text-sm">{incident}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export const TranscriptionHistoryPanel = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('24h');
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [stats, setStats] = useState(null);

  // Fetch transcripts with filters
  const fetchTranscripts = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      
      // Convert time filter to hours
      const hoursMap = { '1h': 1, '6h': 6, '24h': 24, '7d': 168, '30d': 720 };
      params.append('hours', hoursMap[timeFilter] || 24);
      params.append('limit', 50);
      
      const response = await fetch(`${API_URL}/api/safety/transcripts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTranscripts(Array.isArray(data) ? data : data.transcripts || []);
      }
    } catch (error) {
      console.error('Failed to fetch transcripts:', error);
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, priorityFilter, timeFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const hoursMap = { '1h': 1, '6h': 6, '24h': 24, '7d': 168, '30d': 720 };
      const hours = hoursMap[timeFilter] || 24;
      
      const response = await fetch(`${API_URL}/api/safety/transcripts/stats?hours=${hours}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token, timeFilter]);

  useEffect(() => {
    if (isOpen) {
      fetchTranscripts();
      fetchStats();
    }
  }, [isOpen, fetchTranscripts, fetchStats]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOpen) fetchTranscripts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, priorityFilter, timeFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setPriorityFilter('all');
    setTimeFilter('24h');
  };

  const hasActiveFilters = searchQuery || priorityFilter !== 'all' || timeFilter !== '24h';

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[480px] p-0 glass-card border-slate-800">
          <SheetHeader className="sr-only">
            <SheetTitle>Transcription History</SheetTitle>
            <SheetDescription>Search and browse past scanner transcriptions</SheetDescription>
          </SheetHeader>
          
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-teal-400" />
                  Transcription History
                </h3>
                {stats && (
                  <Badge variant="outline" className="text-teal-400 border-teal-500/30">
                    {stats.total_transcripts || 0} total
                  </Badge>
                )}
              </div>
              
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search transcripts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-slate-800 border-slate-700 text-white"
                  data-testid="transcript-search-input"
                />
              </div>
              
              {/* Filters */}
              <div className="flex gap-2">
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-[130px] bg-slate-800 border-slate-700 text-white" data-testid="priority-filter">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="informational">Info</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-[110px] bg-slate-800 border-slate-700 text-white" data-testid="time-filter">
                    <SelectValue placeholder="Time" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="1h">Last Hour</SelectItem>
                    <SelectItem value="6h">Last 6 Hours</SelectItem>
                    <SelectItem value="24h">Last 24 Hours</SelectItem>
                    <SelectItem value="7d">Last 7 Days</SelectItem>
                    <SelectItem value="30d">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
                
                {hasActiveFilters && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </div>
            
            {/* Stats Bar */}
            {stats && (
              <div className="px-4 py-2 bg-slate-800/50 border-b border-slate-700 flex gap-4">
                <div className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-slate-400">Critical: {stats.critical_count || 0}</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span className="text-slate-400">Urgent: {stats.urgent_count || 0}</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-slate-400">Routine: {stats.routine_count || 0}</span>
                </div>
              </div>
            )}
            
            {/* Transcript List */}
            <ScrollArea className="flex-1 p-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
                </div>
              ) : transcripts.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No transcripts found</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'Play a scanner and click "Transcribe Now"'}
                  </p>
                </div>
              ) : (
                transcripts.map((transcript, index) => (
                  <TranscriptHistoryCard
                    key={transcript._id || index}
                    transcript={transcript}
                    onClick={setSelectedTranscript}
                  />
                ))
              )}
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Detail Modal */}
      {selectedTranscript && (
        <TranscriptDetailModal
          transcript={selectedTranscript}
          onClose={() => setSelectedTranscript(null)}
        />
      )}
    </>
  );
};

// Floating button for quick access from dashboard
export const TranscriptionHistoryButton = ({ onClick }) => {
  return (
    <Button
      onClick={onClick}
      className="fixed bottom-24 right-20 z-50 rounded-full w-12 h-12 bg-slate-700 hover:bg-slate-600 shadow-lg"
      title="Transcription History"
      data-testid="transcription-history-btn"
    >
      <History className="w-5 h-5" />
    </Button>
  );
};

export default TranscriptionHistoryPanel;
